/**
 * Trend Monster — Compact Floating HUD
 *
 * Small glass card pinned to top-left corner with neon glow:
 *   ┌─────────────────────────────┐
 *   │ ▲ BUY  55992.00   Active ▲ │  ← title strip
 *   │  E 55725  T1 56004  T2 562 │  ← level grid
 *   │  SL 55307  TSL 55799       │
 *   │  ▓▓▓▓▓░░ Strong    PL +266 │  ← strength + PL
 *   └─────────────────────────────┘
 *
 * BUY → pulsing emerald glow    SELL → pulsing crimson glow
 * CSS prefix: tm-
 */
import React from "react";
import { TrendMonsterDashboard } from "../../indicators/built-in/trend-monster";
import "./TrendMonsterOverlay.scss";

interface ArrowBadge {
  x: number;
  y: number;
  direction: "long" | "short";
  type: string;
  position: string;
}

interface TrendMonsterOverlayProps {
  dashboard: TrendMonsterDashboard | null;
  symbol: string;
  visible: boolean;
  showDashboard: boolean;
  onToggleDashboard: () => void;
  arrowBadges: ArrowBadge[];
}

const TrendMonsterOverlay: React.FC<TrendMonsterOverlayProps> = ({
  dashboard, symbol, visible, showDashboard, onToggleDashboard, arrowBadges,
}) => {
  if (!visible) return null;

  const fmt = (v: number | null) => (v !== null ? v.toFixed(2) : "—");
  const fmtPL = (v: number) => (v !== 0 ? v.toFixed(2) : "—");

  /* ── arrow badges on chart ── */
  const renderBadges = () => (
    <>
      {arrowBadges.map((b, i) => {
        if (b.type === "signal") {
          const isBuy = b.direction === "long";
          const label = isBuy ? "Buy" : "Sell";
          const color = isBuy ? "#00e676" : "#ff1744";
          const bgColor = isBuy ? "rgba(0, 230, 118, 0.18)" : "rgba(255, 23, 68, 0.18)";
          const top = b.position === "below" ? b.y + 44 : b.y - 54;
          return (
            <div key={`tm-sig-${i}`} className="tm-badge" style={{ left: b.x - 20, top, color, borderColor: color, background: bgColor }}>
              {label}
            </div>
          );
        }
        if (b.type === "tp1" || b.type === "tp2") {
          const label = b.type === "tp1" ? "TGT-1 ✓" : "TGT-2 ✓";
          const top = b.position === "above" ? b.y - 38 : b.y + 18;
          return (
            <div key={`tm-tp-${i}`} className="tm-badge tm-badge--tp" style={{ left: b.x - 24, top }}>
              {label}
            </div>
          );
        }
        if (b.type === "sl" || b.type === "tsl") {
          const label = b.type === "sl" ? "SL ✗" : "TSL ✗";
          const top = b.position === "above" ? b.y - 38 : b.y + 18;
          return (
            <div key={`tm-sl-${i}`} className="tm-badge tm-badge--sl" style={{ left: b.x - 20, top }}>
              {label}
            </div>
          );
        }
        return null;
      })}
    </>
  );

  const isLong = dashboard?.trend === "LONG";
  const isShort = dashboard?.trend === "SHORT";
  const mode = isLong ? "buy" : isShort ? "sell" : "neutral";

  /* ── collapsed: tiny floating pill ── */
  if (!showDashboard) {
    return (
      <>
        <div className={`tm-hud tm-hud--${mode} tm-hud--mini`} onClick={onToggleDashboard}>
          <span className="tm-hud__icon">{isLong ? "▲" : isShort ? "▼" : "◆"}</span>
          <span className="tm-hud__mini-label">Trend Monster</span>
          <span className="tm-hud__expand">▼</span>
        </div>
        {renderBadges()}
      </>
    );
  }

  if (!dashboard) return <>{renderBadges()}</>;

  const {
    entry, stopLoss, trailingSL, target1, target2,
    currentPrice, livePL, bookedPL, status, tp1Hit, tp2Hit, slHit, tslHit,
    strengthScore, strengthLabel,
  } = dashboard;

  const dirLabel = isLong ? "BUY" : isShort ? "SELL" : "—";
  const dirIcon = isLong ? "▲" : isShort ? "▼" : "◆";
  const strengthPct = Math.max(0, Math.min(100, (strengthScore / 5) * 100));

  return (
    <>
      <div className={`tm-hud tm-hud--${mode}`}>
        {/* ── glow ring (decorative) ── */}
        <div className={`tm-hud__glow tm-hud__glow--${mode}`} />

        {/* ── title strip ── */}
        <div className="tm-hud__title">
          <span className={`tm-hud__dir tm-hud__dir--${mode}`}>{dirIcon} {dirLabel}</span>
          <span className="tm-hud__price">{currentPrice.toFixed(2)}</span>
          <span className={`tm-hud__status tm-hud__status--${slHit || tslHit ? "sl" : tp1Hit || tp2Hit ? "tp" : "active"}`}>
            {status}
          </span>
          <button className="tm-hud__toggle" onClick={onToggleDashboard} title="Collapse">▲</button>
        </div>

        {/* ── level grid ── */}
        <div className="tm-hud__grid">
          <div className={`tm-hud__cell tm-hud__cell--entry tm-hud__cell--${mode}`}>
            <span className="tm-hud__lbl">ENTRY</span>
            <span className="tm-hud__val">{fmt(entry)}</span>
          </div>
          <div className={`tm-hud__cell ${tp1Hit ? "tm-hud__cell--hit" : ""}`}>
            <span className="tm-hud__lbl">TGT-1</span>
            <span className="tm-hud__val">{fmt(target1)}{tp1Hit ? " ✓" : ""}</span>
          </div>
          <div className={`tm-hud__cell ${tp2Hit ? "tm-hud__cell--hit" : ""}`}>
            <span className="tm-hud__lbl">TGT-2</span>
            <span className="tm-hud__val">{fmt(target2)}{tp2Hit ? " ✓" : ""}</span>
          </div>
          <div className={`tm-hud__cell tm-hud__cell--risk ${slHit ? "tm-hud__cell--sl-hit" : ""}`}>
            <span className="tm-hud__lbl">SL</span>
            <span className="tm-hud__val">{fmt(stopLoss)}{slHit ? " ✗" : ""}</span>
          </div>
          <div className={`tm-hud__cell tm-hud__cell--risk ${tslHit ? "tm-hud__cell--sl-hit" : ""}`}>
            <span className="tm-hud__lbl">TSL</span>
            <span className="tm-hud__val">{fmt(trailingSL)}{tslHit ? " ✗" : ""}</span>
          </div>
        </div>

        {/* ── footer: strength + PL ── */}
        <div className="tm-hud__footer">
          {strengthScore >= 0 && (
            <div className="tm-hud__meter" title={`Strength: ${strengthScore}/5`}>
              <div className="tm-hud__meter-track">
                <div className={`tm-hud__meter-fill tm-hud__meter-fill--${mode}`} style={{ width: `${strengthPct}%` }} />
              </div>
              <span className="tm-hud__meter-txt">{strengthLabel}</span>
            </div>
          )}
          <div className="tm-hud__pl">
            <span className="tm-hud__pl-lbl">Booked</span>
            <span className={`tm-hud__pl-val ${bookedPL >= 0 ? "tm-hud__pl--up" : "tm-hud__pl--dn"}`}>{fmtPL(bookedPL)}</span>
            <span className="tm-hud__pl-sep">·</span>
            <span className="tm-hud__pl-lbl">Live</span>
            <span className={`tm-hud__pl-val ${livePL >= 0 ? "tm-hud__pl--up" : "tm-hud__pl--dn"}`}>{fmtPL(livePL)}</span>
          </div>
        </div>
        {/* ── indicator name row ── */}
        <div className="tm-hud__name-row">Trend Monster</div>
      </div>

      {/* ── arrow badges on chart ── */}
      {renderBadges()}
    </>
  );
};

export default TrendMonsterOverlay;
